import { Injectable } from '@angular/core';
import {
	Action,
	Selector,
	State,
	StateContext,
	select,
	dispatch,
} from '@ngxs/store';
import * as actions from './client-intake.actions';
import {
	FirestoreStateBaseForMatterWithSubscription,
	MatterWithSubscriptionStateModel,
} from '@state/FirestoreStateBaseForMatter';
import { CollectionPaths } from '@models/collections';
import { DEFAULT_STATE_BASE } from '@state/FirestoreStateBase';
import { IntakeFormStatus, ClientIntakeForm } from '@models/client-intake';
import { AuthState } from '@state/auth';
import {
	SetClientIntakeResponsesConnection,
	ClearClientIntakeResponsesConnectionAndState,
} from '@db-access/clientIntakeResponses';
import {
	SetClientIntakeFormItemsConnection,
	ClearClientIntakeFormItemsConnectionAndState,
} from '@db-access/clientIntakeFormItems';
import { getMostRecentActiveForm } from './client-intake.state.utils';
import { ForceAddTagsToFirestore } from '../../global/tags/tags.actions';
import {
	SetClientIntakeFormContextConnection,
	ClearClientIntakeFormContextConnectionAndState,
} from '@db-access/clientIntakeFormContext';
import {
	ClearCurrentIntakeFormConnection,
	SetClientIntakeFormsConnection,
} from '@db-access/clientIntakeForms';

export interface ClientIntakeFormStateModel
	extends MatterWithSubscriptionStateModel {
	items: ClientIntakeForm[];
}

type Ctx = StateContext<ClientIntakeFormStateModel>;

const DEFAULT_STATE: ClientIntakeFormStateModel = {
	...DEFAULT_STATE_BASE,
	items: [],
};

@State<ClientIntakeFormStateModel>({
	name: 'clientIntakeForm',
	defaults: DEFAULT_STATE,
})
@Injectable()
export class MatterClientIntakeFormState extends FirestoreStateBaseForMatterWithSubscription<
	ClientIntakeForm,
	ClientIntakeFormStateModel
> {
	private loggedInUserId_ = select(AuthState.getLoggedInUserId);

	// Dispatch helpers
	private setClientIntakeResponsesConnection = dispatch(
		SetClientIntakeResponsesConnection,
	);

	private clearClientIntakeResponsesConnectionAndState = dispatch(
		ClearClientIntakeResponsesConnectionAndState,
	);

	private setClientIntakeFormItemsConnection = dispatch(
		SetClientIntakeFormItemsConnection,
	);

	private clearClientIntakeFormItemsConnectionAndState = dispatch(
		ClearClientIntakeFormItemsConnectionAndState,
	);

	private setClientIntakeFormContextConnection = dispatch(
		SetClientIntakeFormContextConnection,
	);

	private clearClientIntakeFormContextConnectionAndState = dispatch(
		ClearClientIntakeFormContextConnectionAndState,
	);

	private setClientIntakeFormsConnection = dispatch(
		SetClientIntakeFormsConnection,
	);

	private clearCurrentIntakeFormConnectionAndState = dispatch(
		ClearCurrentIntakeFormConnection,
	);

	@Selector([MatterClientIntakeFormState])
	static getAll(state: ClientIntakeFormStateModel): ClientIntakeForm[] {
		return state?.items ?? [];
	}

	@Selector([MatterClientIntakeFormState])
	static getMostRecentActiveForm(
		state: ClientIntakeFormStateModel,
	): ClientIntakeForm | null {
		return getMostRecentActiveForm(state);
	}

	constructor() {
		super(
			(pathParams) =>
				CollectionPaths.business_matter_client_intake_forms(
					pathParams?.businessId,
					pathParams?.matterId,
				),
			(payload) =>
				this.transformDocToStateWithConnection(payload as ClientIntakeForm[]),
			() => DEFAULT_STATE,
		);
	}

	private transformDocToStateWithConnection(
		payload: ClientIntakeForm[],
	): Partial<ClientIntakeFormStateModel> {
		const stateUpdate = { items: payload };

		this.handleClientIntakeFormsConnectionAfterUpdate(payload);
		this.handleClientIntakeResponsesConnectionAfterUpdate(payload);
		this.handleClientIntakeFormItemsConnectionAfterUpdate(payload);

		return stateUpdate;
	}

	private handleClientIntakeFormsConnectionAfterUpdate(
		items: ClientIntakeForm[],
	): void {
		const businessId = this.businessId;
		const matterId = this.matterId;
		const mostRecentForm = getMostRecentActiveForm({
			items,
		} as ClientIntakeFormStateModel);

		if (businessId && matterId && mostRecentForm?.id) {
			this.setClientIntakeFormsConnection(true, {
				businessId,
				matterId,
				clientIntakeFormId: mostRecentForm.id,
			});
		} else {
			this.clearCurrentIntakeFormConnectionAndState();
		}
	}

	private handleClientIntakeResponsesConnectionAfterUpdate(
		items: ClientIntakeForm[],
	): void {
		const businessId = this.businessId;
		const matterId = this.matterId;
		const mostRecentForm = getMostRecentActiveForm({
			items,
		} as ClientIntakeFormStateModel);

		if (businessId && matterId && mostRecentForm?.id) {
			this.setClientIntakeResponsesConnection(true, {
				businessId,
				matterId,
				clientIntakeFormId: mostRecentForm.id,
			});
		} else {
			this.clearClientIntakeResponsesConnectionAndState();
		}
	}

	private handleClientIntakeFormItemsConnectionAfterUpdate(
		items: ClientIntakeForm[],
	): void {
		const businessId = this.businessId;
		const matterId = this.matterId;
		const mostRecentForm = getMostRecentActiveForm({
			items,
		} as ClientIntakeFormStateModel);

		if (businessId && matterId && mostRecentForm?.id) {
			this.setClientIntakeFormItemsConnection(true, {
				businessId,
				matterId,
				clientIntakeFormId: mostRecentForm.id,
			});

			this.setClientIntakeFormContextConnection(true, {
				businessId,
				matterId,
				clientIntakeFormId: mostRecentForm.id,
			});
		} else {
			this.clearClientIntakeFormItemsConnectionAndState();
			this.clearClientIntakeFormContextConnectionAndState();
		}
	}

	protected clearStateAndSubscription(
		ctx: StateContext<ClientIntakeFormStateModel>,
	): void {
		this.clearCurrentIntakeFormConnectionAndState();
		this.clearClientIntakeResponsesConnectionAndState();
		this.clearClientIntakeFormItemsConnectionAndState();
		this.clearClientIntakeFormContextConnectionAndState();

		super.clearStateAndSubscription(ctx);
	}

	@Action(actions.CreateIntakeForm)
	async createIntakeForm(
		ctx: Ctx,
		{ payload }: actions.CreateIntakeForm,
	): Promise<void> {
		await this.repo.CreateOrMerge(
			payload.clientIntakeForm.id,
			payload.clientIntakeForm,
		);
		// Ensure tags are added to Firestore, hopefully before the go to send the email
		// as the tags are used in the email
		ctx.dispatch(ForceAddTagsToFirestore);
	}

	@Action(actions.UpdateStatus)
	async updateStatus(
		ctx: Ctx,
		{ payload }: actions.UpdateStatus,
	): Promise<void> {
		const loggedInUserId = this.loggedInUserId_();

		const currStatusHistory =
			ctx.getState().items.find((form) => form.id === payload.intakeFormId)
				?.statusHistory ?? [];

		const now = Date.now();
		const updatedStatusHistory = [
			...currStatusHistory,
			// Add the new status to the history
			{
				status: payload.status,
				updatedBy: loggedInUserId,
				updatedAt: now,
			},
		];

		await this.repo.CreateOrMerge(payload.intakeFormId, {
			lastUpdatedAt: now,
			lastUpdatedBy: loggedInUserId,
			status: payload.status as IntakeFormStatus,
			statusHistory: updatedStatusHistory,
		});
	}

	@Action(actions.DeactivateIntakeForm)
	async deactivateIntakeForm(
		ctx: Ctx,
		{ payload }: actions.DeactivateIntakeForm,
	): Promise<void> {
		const loggedInUserId = this.loggedInUserId_();
		const now = Date.now();

		const currStatusHistory =
			ctx.getState().items.find((form) => form.id === payload.intakeFormId)
				?.statusHistory ?? [];

		const updatedStatusHistory = [
			...currStatusHistory,
			{
				status: IntakeFormStatus.CANCELLED,
				updatedBy: loggedInUserId,
				updatedAt: now,
			},
		];

		await this.repo.CreateOrMerge(payload.intakeFormId, {
			lastUpdatedAt: now,
			lastUpdatedBy: loggedInUserId,
			status: IntakeFormStatus.CANCELLED,
			statusHistory: updatedStatusHistory,
		});
	}
}
